/**
 * Fixture data + transport for `?fixture=ncr` — RAW `GET /api/projects/{code}/ncrs`
 * row arrays in the exact shape `azure-functions/cis/shared/ncr.js`
 * (`rowFromSpNcr`) and `CisNcrsQuery/index.js` produce, run through the same
 * `shapeNcrScreen` pipeline (src/features/sm/api/shape.js) the live app
 * uses, plus an in-memory PATCH/raise/resolve store so the screen is fully
 * testable offline (SPEC-CIS-SITE-MANAGER-COCKPIT-V3-001 §4.4/§4.5). Not a
 * general SharePoint simulator — just enough to prove the UI flows; the
 * wire payloads themselves are proven against the real server validators in
 * shape.ncr.realvalidator.test.js.
 *
 * Visible text (titles, locations, wording) for the three "To decide" rows
 * is copied verbatim from mockups/cockpit-v3/NCR.dc.html and reuses the
 * ncrNumbers (501/502/503) and fictional inspector names (j.mendoza,
 * t.aoki, r.fields) already used for the same three NCRs in fixtures/main.js
 * Today rows, so the same NCR looks the same whichever screen it's viewed
 * from.
 *
 * Preview: the live app never approximates recipients/subject/body — it
 * calls the real, pure-read `POST /api/cockpit/ncrs/raise?preview=1` route
 * (`azure-functions/cis/shared/raise.js` `previewProjectRaise`) and renders
 * that response verbatim (App.jsx `handleNcrPreview`, `client.js
 * previewRaise`). `previewRaise` below is this fixture's OWN independent
 * reimplementation of that same function — a browser bundle can't `require`
 * the Node function app's `shared/` modules (Node built-ins, and `raise.js`
 * itself transitively needs the `xlsx` package). Its faithfulness is proven
 * in `shape.ncr.preview.realvalidator.test.js`, which runs the SAME fixture
 * row data and env through the REAL `previewProjectRaise` (via
 * `createRequire`, with a stub Graph-shaped store) and asserts this
 * fixture's output for that input is identical. `FIXTURE_PREVIEW_ENV` below
 * is also passed directly to the real function in that test — one source of
 * truth for the fixture's env, not two definitions to drift apart. Raise
 * itself (the send) is still fixture-only in this build step: no mail is
 * sent, no live Azure calls made.
 */

"use strict";

// Mirrors azure-functions/cis/templates/ncr-raise-email.txt and
// shared/raise.js messageSubject/messageText verbatim (kept in sync by
// inspection, not by import — a browser bundle can't `require` the Node
// function app's shared/ modules). Cross-checked against the real functions
// in shape.ncr.preview.realvalidator.test.js.
const RAISE_EMAIL_TEMPLATE = `Please find the attached non-conformity register and supporting photographs.

{{items}}

Please provide the responsible department's response and corrective action.
{{omittedPhotosNotice}}`;

export function fixtureRaiseSubject(code, rows) {
  const numbers = rows.map((row) => `D${row.ncrNumber}`).join(", ");
  return `${code} · Non-conformity report(s) ${numbers}`.replace(/[\r\n]+/g, " ").slice(0, 255);
}

export function fixtureRaiseCoverText(rows) {
  const items = rows.map((row) => `D${row.ncrNumber} · ${row.title || ""} · ${row.location || ""}`).join("\n");
  return RAISE_EMAIL_TEMPLATE.replace("{{items}}", items).replace("{{omittedPhotosNotice}}", "").trim();
}

// Fixture-only Function App settings (azure-functions/cis/shared/mail-send.js
// mailboxForProject / CIS_YARD_QA__<CODE>) — fictional NEG-internal-domain
// sample addresses, not real yard mailboxes, so the fixture's preview can
// reach a "ready" status through the SAME domain gate
// (recipient-gate.js assertRecipientsAllowed, default-allowed domain
// neg-marine.com) the real route enforces, rather than always showing
// external_send_blocked for a made-up yard domain.
export const FIXTURE_PREVIEW_ENV = {
  CIS_PROJECT_MAILBOX__SS412413: "projects-ss412413@neg-marine.com",
  CIS_YARD_QA__SS412413: "qa2@neg-marine.com",
  CIS_PROJECT_MAILBOX__SS418: "projects-ss418@neg-marine.com",
  CIS_YARD_QA__SS418: "qa1@neg-marine.com",
  CIS_PROJECT_MAILBOX__SS411: "projects-ss411@neg-marine.com",
  CIS_YARD_QA__SS411: "qa3@neg-marine.com",
  EMAIL_ALLOWED_RECIPIENT_DOMAINS: "neg-marine.com",
};

function ncrRow(overrides) {
  return {
    ncrNumber: overrides.ncrNumber,
    clientNcrId: overrides.clientNcrId || "",
    title: overrides.title || "",
    description: overrides.description || "",
    location: overrides.location || "",
    issuer: overrides.issuer || "",
    raisedDate: overrides.raisedDate || "",
    dutyDept: overrides.dutyDept || "",
    assignedTo: overrides.assignedTo || "",
    yardAnswer: overrides.yardAnswer || "",
    yardAnswerDate: overrides.yardAnswerDate || "",
    status: overrides.status,
    closureNote: overrides.closureNote || "",
    sourceInspectionNumber: overrides.sourceInspectionNumber || 0,
    sourceClientCaptureId: overrides.sourceClientCaptureId || "",
    reinspectionNumbers: overrides.reinspectionNumbers || "",
    sourceDefectId: overrides.sourceDefectId || "",
    source: overrides.source || "pwa",
    createdByUpn: overrides.createdByUpn || "",
    createdAtUtc: overrides.createdAtUtc || "2026-08-25T07:00:00.000Z",
    lastChangedUtc: overrides.lastChangedUtc || "2026-08-25T07:00:00.000Z",
    raisedToYardAtUtc: overrides.raisedToYardAtUtc || "",
    raiseRunId: overrides.raiseRunId || "",
    returnNote: overrides.returnNote || "",
    returnedAtUtc: overrides.returnedAtUtc || "",
    photoCount: overrides.photoCount || 0,
    etag: overrides.etag || `"etag-ncr-${overrides.ncrNumber}"`,
  };
}

// The three "To decide" rows — same ncrNumbers as fixtures/main.js
// ncrsRecorded so Today's "Review" hand-off (which carries { code,
// ncrNumber }) opens this screen on the same real row.
const SS412413_TODECIDE = ncrRow({
  ncrNumber: 501,
  title: "Undersize weld leg, port bracket fr.88",
  location: "Block 42P, hull",
  description:
    "Fillet welds on port bracket at frame 88, block 42P, measured at 5.5 mm leg length against the 7 mm specified on H-42P-118. Request rectification per WPS and re-presentation for inspection.",
  raisedDate: "2026-08-27",
  createdByUpn: "j.mendoza@example.com",
  status: "Recorded",
  photoCount: 3,
});
const SS418_TODECIDE = ncrRow({
  ncrNumber: 502,
  title: "Duct hanger missing, accomm. HVAC",
  location: "Accommodation, HVAC",
  description: "Duct hanger missing at accommodation HVAC run, deck 3. Request fit-up per drawing and re-presentation for inspection.",
  raisedDate: "2026-08-29",
  createdByUpn: "t.aoki@example.com",
  status: "Recorded",
  photoCount: 2,
});
const SS411_TODECIDE = ncrRow({
  ncrNumber: 503,
  title: "Coating holiday, A-deck aft",
  location: "A-deck aft",
  description: "Coating holiday (pinhole) found on A-deck aft during DFT survey. Request touch-up per coating spec and re-presentation for inspection.",
  raisedDate: "2026-08-31",
  createdByUpn: "r.fields@example.com",
  status: "Recorded",
  photoCount: 1,
});

function extraRows(code, prefix, count, status, extra) {
  const rows = [];
  for (let i = 0; i < count; i += 1) {
    rows.push(
      ncrRow({
        ncrNumber: 600 + prefix * 100 + i,
        title: `Sample non-conformity ${i + 1}`,
        location: "General",
        description: "Fixture-only row for tab counts; not part of the artboard's own visible sample.",
        raisedDate: "2026-08-20",
        createdByUpn: code === "SS412413" ? "j.mendoza@example.com" : code === "SS418" ? "t.aoki@example.com" : "r.fields@example.com",
        status,
        ...extra,
      })
    );
  }
  return rows;
}

// Raised: 21 total across projects (matches the artboard's "Raised, awaiting
// yard 21" tab count). Answered: 2 total. Closed: 5 total (Closed + Cancelled
// mix) — the artboard shows no count for this tab, so its total is not
// pinned to any sample text.
function buildProjectRows() {
  return {
    SS412413: [
      SS412413_TODECIDE,
      ...extraRows("SS412413", 1, 8, "Open", { raisedToYardAtUtc: "2026-08-20T09:00:00.000Z", raiseRunId: "" }),
      ...extraRows("SS412413", 1, 1, "Answered", { raisedToYardAtUtc: "2026-08-15T09:00:00.000Z", yardAnswer: "Rectified.", yardAnswerDate: "2026-08-22" }),
      ...extraRows("SS412413", 1, 2, "Closed", { raisedToYardAtUtc: "2026-08-10T09:00:00.000Z", yardAnswer: "Rectified.", yardAnswerDate: "2026-08-14" }),
    ],
    SS418: [
      SS418_TODECIDE,
      ...extraRows("SS418", 2, 7, "Open", { raisedToYardAtUtc: "2026-08-21T09:00:00.000Z" }),
      ...extraRows("SS418", 2, 1, "Answered", { raisedToYardAtUtc: "2026-08-16T09:00:00.000Z", yardAnswer: "Repaired.", yardAnswerDate: "2026-08-23" }),
      ...extraRows("SS418", 2, 2, "Cancelled", { closureNote: "Duplicate entry", source: "pwa" }),
    ],
    SS411: [
      SS411_TODECIDE,
      ...extraRows("SS411", 3, 6, "Open", { raisedToYardAtUtc: "2026-08-19T09:00:00.000Z" }),
      ...extraRows("SS411", 3, 1, "Closed", { raisedToYardAtUtc: "2026-08-09T09:00:00.000Z", yardAnswer: "Touched up.", yardAnswerDate: "2026-08-13" }),
    ],
  };
}

/** RAW per-project `GET /api/projects/{code}/ncrs` envelopes, as `{ projects: [{ code, rows }], errors: [] }`. */
export function buildFixtureNcrProjects() {
  const rows = buildProjectRows();
  return {
    projects: Object.entries(rows).map(([code, projectRows]) => ({ code, rows: projectRows })),
    errors: [],
  };
}

export const fixtureNcrProjects = buildFixtureNcrProjects();

/**
 * In-memory PATCH/raise/resolve store, same discipline as
 * fixtures/plan.js#createFixturePlanStore: a thin, independent
 * reimplementation for offline UX testing. Enforces the same shape as the
 * real endpoints (etag compare-and-swap, `Recorded`-only field refusal,
 * `raise_in_progress`, `raiseId` idempotency) so the screen's flows can be
 * exercised end to end without a network call.
 */
export function createFixtureNcrStore() {
  let byCode = buildProjectRows();
  const raiseReports = new Map(); // runId -> { status, items }
  // SPEC-CIS-NCR-RETURN-RESUBMIT-001: returnId -> history row, keyed by
  // `${code}:${ncrNumber}` for the GET .../returns fixture.
  const returnsByKey = new Map();
  let nextReturnId = 1;

  function findRow(code, ncrNumber) {
    return (byCode[code] || []).find((r) => r.ncrNumber === ncrNumber);
  }

  function historyKey(code, ncrNumber) {
    return `${code}:${ncrNumber}`;
  }

  async function fetchNcrs(code) {
    return { status: 200, ok: true, body: byCode[code] || [] };
  }

  async function patchNcr(code, ncrNumber, body) {
    const row = findRow(code, Number(ncrNumber));
    if (!row) return { status: 404, ok: false, body: { errorCode: "ncr_not_found" } };
    if (body.etag !== row.etag) return { status: 409, ok: false, body: { errorCode: "stale_etag", row } };
    if (row.status === "Recorded" && row.raiseRunId) {
      return { status: 422, ok: false, body: { errorCode: "raise_in_progress" } };
    }
    if (row.status === "Recorded") {
      for (const field of ["dutyDept", "yardAnswer", "yardAnswerDate"]) {
        if (body[field] !== undefined) {
          return { status: 422, ok: false, body: { errorCode: "invalid_transition", errors: [[field, "not allowed before raise"]] } };
        }
      }
    }
    if (body.status !== undefined && body.status !== row.status) {
      if (body.status === "Cancelled" && row.status === "Recorded") {
        if (!body.closureNote) return { status: 422, ok: false, body: { errorCode: "invalid_transition", errors: [["closureNote", "reason required to dismiss"]] } };
        row.status = "Cancelled";
        row.closureNote = body.closureNote;
      } else {
        return { status: 422, ok: false, body: { errorCode: "invalid_transition" } };
      }
    }
    if (body.returnNote !== undefined) {
      if (!body.returnNote) return { status: 422, ok: false, body: { errorCode: "invalid_transition", errors: [["returnNote", "required"]] } };
      row.returnNote = body.returnNote;
      row.returnedAtUtc = new Date().toISOString();
      // SPEC-CIS-NCR-RETURN-RESUBMIT-001 §4.4: append-only history row + the
      // authoritative pointer, mirroring the real server's shape.
      const key = historyKey(code, ncrNumber);
      const list = returnsByKey.get(key) || [];
      const newReturn = {
        returnId: nextReturnId++,
        returnNote: body.returnNote,
        returnedByUpn: "sm@fixture.invalid",
        returnedAtUtc: row.returnedAtUtc,
        status: "Open",
      };
      for (const prior of list) if (prior.returnId === row.openReturnId) prior.status = "Superseded";
      list.push(newReturn);
      returnsByKey.set(key, list);
      row.awaitingResubmit = true;
      row.openReturnId = newReturn.returnId;
    }
    if (body.description !== undefined) row.description = body.description;
    if (body.title !== undefined) row.title = body.title;
    if (body.location !== undefined) row.location = body.location;
    row.lastChangedUtc = new Date().toISOString();
    row.etag = `"etag-ncr-${row.ncrNumber}-${Date.now()}"`;
    return { status: 200, ok: true, body: row };
  }

  /** Fixture reproduction of POST /api/cockpit/ncrs/raise — one report per project per raiseId, sent immediately (no ambiguous/failed paths, since raise is fixture-only this build step). */
  async function raiseNcrs(body) {
    const projects = [];
    const byProjectCode = new Map();
    for (const item of body.items) {
      if (!byProjectCode.has(item.code)) byProjectCode.set(item.code, []);
      byProjectCode.get(item.code).push(item);
    }
    for (const [code, items] of byProjectCode) {
      const runId = `${body.raiseId}-${code}`;
      const existing = raiseReports.get(runId);
      if (existing && existing.status === "sent") {
        projects.push({ code, status: "duplicate", runId, attempt: existing.attempt, items: [] });
        continue;
      }
      const stale = items.find((item) => {
        const row = findRow(code, item.ncrNumber);
        return !row || row.etag !== item.etag || row.status !== "Recorded";
      });
      if (stale) {
        projects.push({ code, status: "failed", runId, attempt: 1, items: [], error: "stale_etag" });
        continue;
      }
      for (const item of items) {
        const row = findRow(code, item.ncrNumber);
        if (item.description !== undefined) row.description = item.description;
        row.status = "Open";
        row.raisedToYardAtUtc = new Date().toISOString();
        row.raiseRunId = runId;
        row.etag = `"etag-ncr-${row.ncrNumber}-${Date.now()}"`;
      }
      raiseReports.set(runId, { status: "sent", attempt: 1, items });
      projects.push({ code, status: "sent", runId, attempt: 1, items: items.map((i) => ({ ncrNumber: i.ncrNumber, status: "raised" })) });
    }
    return { status: 200, ok: true, body: { raiseId: body.raiseId, projects, errors: projects.filter((p) => p.error).map((p) => ({ code: p.code, error: p.error })) } };
  }

  async function resolveRaise(runId, body) {
    const report = raiseReports.get(runId);
    if (!report || report.status !== "sending") return { status: 422, ok: false, body: { errorCode: "invalid_transition" } };
    report.status = body.outcome === "sent" ? "sent" : "failed";
    return { status: 200, ok: true, body: { runId, status: report.status } };
  }

  /**
   * Fixture reproduction of `POST /api/cockpit/ncrs/raise?preview=1` — a
   * pure read, never mutates `byCode`. Field-for-field reimplementation of
   * `previewProjectRaise` (azure-functions/cis/shared/raise.js:101-132);
   * `previewProjectFixture` below is exported unbound from `byCode` so
   * shape.ncr.preview.realvalidator.test.js can drive the identical
   * function against the real one with its own stub store.
   */
  async function previewRaise(body) {
    const byProjectCode = new Map();
    for (const item of body.items) {
      if (!byProjectCode.has(item.code)) byProjectCode.set(item.code, []);
      byProjectCode.get(item.code).push(item);
    }
    const projects = [];
    for (const [code, items] of byProjectCode) {
      projects.push(previewProjectFixture(code, body.raiseId, items, (c, n) => {
        const row = findRow(c, n);
        return row
          ? { itemId: row.ncrNumber, etag: row.etag, fields: { NcrStatus: row.status, RaiseRunId: row.raiseRunId || "", Title: row.title, Location: row.location, Description: row.description } }
          : null;
      }));
    }
    return { status: 200, ok: true, body: { raiseId: body.raiseId, projects, errors: projects.filter((p) => p.error).map((p) => ({ code: p.code, error: p.error })) } };
  }

  /** Fixture reproduction of `GET .../ncrs/{ncrNumber}/returns` (§5) — oldest first. */
  async function fetchNcrReturns(code, ncrNumber) {
    const rows = returnsByKey.get(historyKey(code, Number(ncrNumber))) || [];
    return { status: 200, ok: true, body: [...rows].sort((a, b) => a.returnId - b.returnId) };
  }

  return { fetchNcrs, patchNcr, fetchNcrReturns, raiseNcrs, resolveRaise, previewRaise };
}

/**
 * The fixture's independent reimplementation of `previewProjectRaise`
 * (azure-functions/cis/shared/raise.js:101-132), parameterised over a
 * `getRow(code, ncrNumber) -> { itemId, etag, fields } | null` lookup so
 * both the browser-side fixture store above and the Node cross-check test
 * (with its own stub store over the same row data) call the exact same
 * logic. Field names in the returned object match `previewProjectRaise`'s
 * real return shape exactly: `{ code, status, runId, items, recipients:
 * { from, to, cc }, subject, coverText, devSinkApplied }` on success, or
 * `{ code, status: "failed", runId, items: [], error }` on any of the same
 * failure codes the real function returns (`no_yard_recipient`,
 * `stale_etag`) — `external_send_blocked`/`mail_unavailable` require the
 * real domain gate / mail-send policy, which this fixture does not
 * reimplement (env is fixed to a domain that always passes it).
 */
export function previewProjectFixture(code, raiseId, items, getRow) {
  const runId = `${raiseId}-${code}`;
  const from = FIXTURE_PREVIEW_ENV[`CIS_PROJECT_MAILBOX__${code}`] || "";
  const toRaw = FIXTURE_PREVIEW_ENV[`CIS_YARD_QA__${code}`] || "";
  const to = toRaw ? [toRaw] : [];
  if (!to.length) return { code, status: "failed", runId, items: [], error: "no_yard_recipient" };
  const rows = [];
  for (const requested of items) {
    const found = getRow(code, requested.ncrNumber);
    if (!found || found.fields.NcrStatus !== "Recorded" || found.fields.RaiseRunId || found.etag !== requested.etag) {
      return { code, status: "failed", runId, items: [], error: "stale_etag" };
    }
    rows.push({
      ncrNumber: Number(found.itemId),
      title: found.fields.Title,
      location: found.fields.Location,
      description: requested.description === undefined ? found.fields.Description : requested.description,
      etag: found.etag,
    });
  }
  const coverRows = rows.map(({ ncrNumber, title, location }) => ({ ncrNumber, title, location }));
  return {
    code,
    status: "ready",
    runId,
    items: rows,
    recipients: { from, to, cc: [from] },
    subject: fixtureRaiseSubject(code, coverRows),
    coverText: fixtureRaiseCoverText(coverRows),
    devSinkApplied: false,
  };
}
